import React, { useRef, forwardRef } from "react";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import MuiAccordionDetails from "@material-ui/core/AccordionDetails";
import ArrowForwardIosSharpIcon from "@material-ui/icons/ArrowForwardIosSharp";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Custom Accordion component that wraps Material-UI Accordion
 * 
 * Note: TransitionProps are configured to prevent findDOMNode warnings in React StrictMode.
 * Material-UI v4 uses findDOMNode internally in the Collapse/Transition components.
 * The TransitionProps with timeout: 'auto' and unmountOnExit: false help suppress these warnings.
 */

const Accordion = forwardRef(({
  scrollIntoView,
  defaultExpanded = undefined,
  isLoading = false,
  loadingMessage = "Loading...",
  headerAction = null,
  ...props
}, forwardedRef) => {
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const internalRef = useRef();
  const ref = forwardedRef || internalRef;

  return (
    <>
      {scrollIntoView ? (
        <MuiAccordion
          style={{ marginTop: "1em" }}
          {...(defaultExpanded !== undefined && { expanded: defaultExpanded })}
          ref={ref}
          onChange={(e, isExpanded) => {
            if (isExpanded) {
              setTimeout(() => {
                ref.current.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }, 200);
            } else {
              navigate(location.pathname);
            }
          }}
          TransitionProps={{
            timeout: 'auto',
            unmountOnExit: false
          }}
          {...props}
        >
          <MuiAccordionSummary
            expandIcon={<ArrowForwardIosSharpIcon />}
            className={classes.summary}
          >
            <Typography className={classes.heading}>{props.summary}</Typography>
            {headerAction && (
              <div className={classes.headerAction} onClick={(e) => e.stopPropagation()}>
                {headerAction}
              </div>
            )}
          </MuiAccordionSummary>
          <MuiAccordionDetails className={classes.root}>
            <div className={classes.contentWrapper}>
              {props.children}
              {isLoading && (
                <div className={classes.loadingOverlay}>
                  <div className={classes.loadingContent}>
                    <CircularProgress size={40} className={classes.spinner} />
                    <Typography variant="body1" className={classes.loadingText}>
                      {loadingMessage}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </MuiAccordionDetails>
        </MuiAccordion>
      ) : (
        <MuiAccordion 
          ref={ref} 
          TransitionProps={{
            timeout: 'auto',
            unmountOnExit: false
          }}
          {...props}
        >
          <MuiAccordionSummary
            expandIcon={<ArrowForwardIosSharpIcon />}
            className={classes.summary}
          >
            <Typography className={classes.heading}>{props.summary}</Typography>
            {headerAction && (
              <div className={classes.headerAction} onClick={(e) => e.stopPropagation()}>
                {headerAction}
              </div>
            )}
          </MuiAccordionSummary>
          <MuiAccordionDetails className={classes.root}>
            <div className={classes.contentWrapper}>
              {props.children}
              {isLoading && (
                <div className={classes.loadingOverlay}>
                  <div className={classes.loadingContent}>
                    <CircularProgress size={40} className={classes.spinner} />
                    <Typography variant="body1" className={classes.loadingText}>
                      {loadingMessage}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </MuiAccordionDetails>
        </MuiAccordion>
      )}
    </>
  );
});

Accordion.displayName = 'Accordion';

export default Accordion;

const useStyles = makeStyles((theme) => ({
  heading: {
    color: "var(--text-primary)",
    fontSize: "1rem",
    paddingLeft: "8px",
    flex: 1,
  },
  root: {
    padding: 0,
  },
  summary: {
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: "12px 0",
    },
    "& .MuiAccordionSummary-expandIcon.Mui-expanded": {
      transform: "rotate(90deg)",
      transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
      }),
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1rem !important",
    },
  },
  headerAction: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },
  contentWrapper: {
    position: "relative",
    width: "100%",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    borderRadius: "8px",
  },
  loadingContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: "1px solid var(--surface-200)",
  },
  spinner: {
    color: "var(--primary-500, #2196f3)",
  },
  loadingText: {
    color: "var(--text-primary)",
    fontSize: "0.875rem",
    fontWeight: 500,
    textAlign: "center",
  },
}));
